import React, {useEffect, useRef, useState} from 'react';
import {Box, Typography, CircularProgress} from '@mui/material';
import {DndContext, useSensor, useSensors, PointerSensor, closestCenter, DragOverlay} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import SortableItem from './SortableItem';
import AnnotationGroup from './AnnotationGroup';
import AnnotationItem, {EditableField} from './AnnotationItem';
import GroupContextMenu from './GroupContextMenu';
import useResizable from './useResizable';
import useDragDropHandlers from './useDragDropHandlers';
import {getAnnotationKey, getTopY} from './annotationUtils';
import {useTheme} from '@mui/material/styles';
import {lighten} from '@mui/material/styles';
import {useTranslation} from "react-i18next";


function ResizablePanel({
                            selectedAnnotations = [],
                            onRemoveAnnotation,
                            onUpdateAnnotation,
                            setAnnotations,
                            annotationOrder,
                            setAnnotationOrder,
                            groupAnnotationOrder,
                            setGroupAnnotationOrder,
                            groupMeta,
                            setGroupMeta,
                            onPeek,
                            onHover,
                            hoveredKey,
                            tableWarningText
                        }) {
    const {t} = useTranslation();

    const theme = useTheme();

    const [width, setWidth] = useState(500);
    const MIN_PANEL_WIDTH = 360;
    const MAX_PANEL_WIDTH = 900;
    const containerRef = useRef(null);
    const resizerRef = useRef(null);

    const [editing, setEditing] = useState({});
    const [editableAnnotations, setEditableAnnotations] = useState({});
    const [backupValues, setBackupValues] = useState({});
    const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
    const [activeGroupId, setActiveGroupId] = useState(null);
    const [activeId, setActiveId] = useState(null);
    const [draggedElement, setDraggedElement] = useState(null);

    // Is any field of this annotation id currently being edited?
    const isAnyFieldEditing = React.useCallback((id) => {
        if (!id) return false;
        const prefix = `${id}_`;
        for (const k in editing) {
            if (editing[k] && k.startsWith(prefix)) return true;
        }
        return false;
    }, [editing]);

    const singleClickTimerRef = useRef(null);
    const schedulePeek = (idOrIds) => {
        window.clearTimeout(singleClickTimerRef.current);
        singleClickTimerRef.current = window.setTimeout(() => {
            const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
            // 🚫 don’t jiggle items that are currently being edited
            const filtered = ids.filter((id) => !isAnyFieldEditing(id));
            if (!filtered.length) return;
            onPeek?.(Array.isArray(idOrIds) ? filtered : filtered[0]);
        }, 220); // ~double-click threshold
    };
    const cancelPeek = () => {
        window.clearTimeout(singleClickTimerRef.current);
    };
    // Custom hook for panel resizing
    useResizable(containerRef, resizerRef, width, setWidth, MIN_PANEL_WIDTH, MAX_PANEL_WIDTH);

    // Dnd-kit sensors configuration
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    // Custom hook for drag and drop logic
    const {handleDragEnd: dndHandleDragEnd, END_DROP_ID} = useDragDropHandlers({
        annotationOrder,
        setAnnotationOrder,
        groupAnnotationOrder,
        setGroupAnnotationOrder,
        selectedAnnotations,
        setAnnotations,
        setGroupMeta,
    });

    // Event handlers for group context menu
    const handleGroupMenuOpen = (event, groupId) => {
        setGroupMenuAnchor(event.nativeEvent.target);
        setActiveGroupId(groupId);
    };

    const handleGroupMenuClose = () => {
        setGroupMenuAnchor(null);
        setActiveGroupId(null);
    };

    // Handler for deleting an annotation (unselecting it)
    const handleDelete = (annotation) => onRemoveAnnotation(annotation);

    // Grouping logic for rendering
    const grouped = {};
    selectedAnnotations.forEach((a) => {
        const key = getAnnotationKey(a);
        if (a.groupId) {
            if (!grouped[a.groupId]) grouped[a.groupId] = {
                name: groupMeta[a.groupId]?.name || a.groupId,
                annotations: []
            };
            grouped[a.groupId].annotations.push(a);
        }
    });

    // Render function for editable fields (used by AnnotationItem and for group name)

    const renderEditableField = (key, fieldLabel, fieldValue, fieldKey, inline = false, bold = false) => {
        const isEditing = editing[`${key}_${fieldKey}`];

        const editedValue = editableAnnotations[key]?.[fieldKey] ?? fieldValue;

        const handleChange = (val) => {
            setEditableAnnotations((prev) => ({
                ...prev,
                [key]: {...prev[key], [fieldKey]: val},
            }));
        };

        const handleConfirm = () => {
                setEditing((prev) => ({...prev, [`${key}_${fieldKey}`]: false}));

                // Special case: group name update
                if (key.startsWith("group_") && fieldKey === "name") {
                    const groupId = key.replace("group_", "");
                    const newName = editedValue;
                    if (newName) {
                        setEditableAnnotations(prev => {
                            const updated = {...prev};
                            delete updated[key];
                            return updated;
                        });
                        setBackupValues(prev => {
                            const updated = {...prev};
                            delete updated[`${key}_${fieldKey}`];
                            return updated;
                        });
                        setEditing(prev => {
                            const updated = {...prev};
                            delete updated[`${key}_${fieldKey}`];
                            return updated;
                        });

                        setGroupMeta(prev => ({
                            ...prev,
                            [groupId]: {name: newName}
                        }));
                    }
                    return;
                }

                // Normal annotation update logic
                const annotation =
                    selectedAnnotations.find(a => getAnnotationKey(a) === key || a.__uid === key);
                const changes = editableAnnotations[key];
                if (!annotation || !changes) return;

                let updatePayload = {};

                if (annotation.type === 'key_value') {
                    updatePayload = {
                        field: changes.field ?? annotation.field?.text,
                        value: changes.value ?? annotation.value?.text
                    };
                } else if (annotation.type === 'entity') {
                    if (fieldKey === 'field') {
                        updatePayload = {entity_type: changes.field ?? annotation.entity_type};
                    } else if (fieldKey === 'value') {
                        updatePayload = {text: changes.value ?? annotation.text};
                    }
                } else if (annotation.type === 'summary_row') {
                    updatePayload = {
                        label: changes.label ?? annotation.label,
                        text: changes.text ?? annotation.text
                    };
                } else if (annotation.type === 'table') {
                    const newChanges = changes || {};
                    let updatePayload = {};

                    // Label edit
                    if (fieldKey === 'label' && newChanges.label !== undefined) {
                        updatePayload = {label: newChanges.label};
                    }
                    // Header cell edit: header_0, header_1, ...
                    else if (fieldKey?.startsWith('header_')) {
                        const ci = parseInt(fieldKey.split('_')[1], 10);
                        if (!Number.isNaN(ci)) {
                            const headers = Array.isArray(annotation.table_header)
                                ? [...annotation.table_header]
                                : [];
                            while (headers.length <= ci) headers.push(`Column ${headers.length + 1}`);
                            headers[ci] = newChanges[fieldKey] ?? headers[ci];
                            updatePayload = {table_header: headers};
                        }
                    }
                    // Row id / row label edit: rowid_0, rowid_1, ...
                    else if (fieldKey?.startsWith('rowid_')) {
                        const ri = parseInt(fieldKey.split('_')[1], 10);
                        if (!Number.isNaN(ri)) {
                            const rowHeader = Array.isArray(annotation.row_header)
                                ? [...annotation.row_header]
                                : [];
                            while (rowHeader.length <= ri) rowHeader.push(String(ri + 1));
                            rowHeader[ri] = newChanges[fieldKey] ?? rowHeader[ri];
                            updatePayload = {row_header: rowHeader};
                        }
                    }
                    // Body cell edit: cell_ri_ci
                    else if (fieldKey?.startsWith('cell_')) {
                        const [, rStr, cStr] = fieldKey.split('_');
                        const ri = parseInt(rStr, 10);
                        const ci = parseInt(cStr, 10);
                        if (!Number.isNaN(ri) && !Number.isNaN(ci)) {
                            const rowsCopy = Array.isArray(annotation.rows) ? annotation.rows.map(r => [...r]) : [];
                            while (rowsCopy.length <= ri) rowsCopy.push([]);
                            while (rowsCopy[ri].length <= ci) rowsCopy[ri].push('');
                            rowsCopy[ri][ci] = newChanges[fieldKey] ?? rowsCopy[ri][ci];
                            updatePayload = {rows: rowsCopy};
                        }
                    }

                    if (Object.keys(updatePayload).length > 0) onUpdateAnnotation(key, updatePayload);

                    // Apply structural patch coming from TableEditor (e.g., after add/move/delete)
                    const {table_header, rows, label, row_header} = newChanges;
                    const patch = {};
                    if (Array.isArray(table_header)) patch.table_header = table_header;
                    if (Array.isArray(rows)) patch.rows = rows;
                    if (typeof label === 'string') patch.label = label;
                    if (Array.isArray(row_header)) patch.row_header = row_header;
                    if (Object.keys(patch).length > 0) onUpdateAnnotation(key, patch);
                }

                if (Object.keys(updatePayload).length > 0) {
                    onUpdateAnnotation(key, updatePayload);
                }
            }
        ;


        const handleCancel = () => {
            setEditableAnnotations((prev) => ({
                ...prev,
                [key]: {...prev[key], [fieldKey]: backupValues[`${key}_${fieldKey}`]},
            }));
            setEditing((prev) => ({...prev, [`${key}_${fieldKey}`]: false}));
        };

        const handleStartEdit = () => {
            setEditing((prev) => ({...prev, [`${key}_${fieldKey}`]: true}));
            setBackupValues((prev) => ({...prev, [`${key}_${fieldKey}`]: fieldValue}));
        };

        const pushStructuralPatch = (patchObj) => {
            setEditableAnnotations((prev) => ({
                ...prev,
                [key]: {...(prev[key] || {}), ...patchObj},
            }));
            // Immediately "confirm" it so it applies without the user pressing Enter:
            // We can call onUpdateAnnotation directly here to avoid waiting for field confirm:
            onUpdateAnnotation(key, patchObj);
        };

        return (
            <EditableField
                panelWidth={width}
                isEditing={isEditing}
                editedValue={editedValue}
                fieldLabel={fieldLabel}
                originalValue={fieldValue}
                fieldKey={fieldKey}
                bold={bold}
                inline={inline}
                onChange={handleChange}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                onStartEdit={handleStartEdit}
            />
        );

    };

    // Synchronize groupAnnotationOrder when selectedAnnotations change
    useEffect(() => {
        const newGroupOrder = {...groupAnnotationOrder}; // Take current state snapshot
        let changed = false;

        selectedAnnotations.forEach(a => {
            const key = getAnnotationKey(a);
            if (a.groupId) {
                if (!newGroupOrder[a.groupId]) {
                    newGroupOrder[a.groupId] = [key];
                    changed = true;
                } else if (!newGroupOrder[a.groupId].includes(key)) {
                    newGroupOrder[a.groupId] = [...newGroupOrder[a.groupId], key];
                    changed = true;
                }
            }
        });

        for (const groupId in newGroupOrder) {
            const hasSelectedAnnotationInGroup = selectedAnnotations.some(a => a.groupId === groupId);
            if (!hasSelectedAnnotationInGroup && !annotationOrder.includes(`group_${groupId}`)) {
                delete newGroupOrder[groupId];
                changed = true;
            } else {
                const filteredGroupMembers = (newGroupOrder[groupId] || []).filter(id =>
                    selectedAnnotations.some(a => getAnnotationKey(a) === id && a.groupId === groupId)
                );
                if (filteredGroupMembers.length !== (newGroupOrder[groupId] || []).length) {
                    newGroupOrder[groupId] = filteredGroupMembers;
                    changed = true;
                }
            }
        }

        if (changed) {
            setGroupAnnotationOrder(newGroupOrder);
        }
    }, [selectedAnnotations, annotationOrder]);


    // Determine the final render order including groups and individual items
    const usedIds = new Set();
    const finalRenderOrder = annotationOrder.flatMap(id => {
        if (usedIds.has(id)) return [];
        usedIds.add(id);

        if (id.startsWith('group_')) {
            const groupId = id.replace('group_', '');
            const groupItems = (groupAnnotationOrder[groupId] || []).filter(childId => {
                if (usedIds.has(childId)) return false;
                usedIds.add(childId);
                return true;
            });
            return [`group_${groupId}`, ...groupItems];
        }
        return [id];
    });


    return (
        <>
            {/* Resizer with generous hit area */}
            <Box
                ref={resizerRef}
                sx={{
                    position: 'relative',
                    width: '4px',
                    cursor: 'col-resize',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: '-6px',
                        right: '-6px',   // 16px total hit area
                    },
                    backgroundColor: 'transparent',
                    '&:hover': {backgroundColor: 'action.hover'}
                }}
            />
            <Box
                ref={containerRef}
                width={width}
                minWidth={MIN_PANEL_WIDTH}
                p={1.5}
                backgroundColor={theme.palette.background.paper}
                sx={{
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    overflowX: 'hidden',   // no horizontal scrollbar
                    overflowY: 'auto',     // vertical scroll only
                }}
            >
                <Typography variant="subtitle1" gutterBottom sx={{mb: 1}}>
                    {t("selected_extractions")}
                </Typography>
                {selectedAnnotations.length > 0 ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={(event) => {
                            setActiveId(event.active.id);
                            // Optional: If you want to clone the actual element for DragOverlay
                            const dragged = document.querySelector(`[data-id="${event.active.id}"]`);
                            if (dragged) setDraggedElement(dragged.cloneNode(true));
                        }}
                        onDragEnd={(event) => {
                            setActiveId(null);
                            setDraggedElement(null); // Clear cloned element after drag
                            dndHandleDragEnd(event); // Call the main drag logic from the hook
                        }}
                    >
                        <SortableContext items={[...finalRenderOrder, END_DROP_ID]}
                                         strategy={verticalListSortingStrategy}>
                            {finalRenderOrder.map((id) => {
                                if (id.startsWith('group_')) {
                                    const groupId = id.replace('group_', '');
                                    const group = grouped[groupId];
                                    if (!group) return null; // Don't render group if no annotations are associated

                                    return (
                                        <AnnotationGroup
                                            key={id}
                                            id={id}
                                            groupId={groupId}
                                            groupName={group.name}
                                            annotations={group.annotations} // Pass the actual annotations in the group
                                            groupAnnotationOrder={groupAnnotationOrder}
                                            selectedAnnotations={selectedAnnotations} // Pass for child filtering
                                            renderEditableField={renderEditableField}
                                            handleDelete={handleDelete}
                                            handleGroupMenuOpen={handleGroupMenuOpen}
                                            onTablePatch={(childId, patch) => onUpdateAnnotation(childId, patch)}
                                            schedulePeek={schedulePeek}
                                            cancelPeek={cancelPeek}
                                            isEditingId={isAnyFieldEditing}
                                            onHover={onHover}
                                            tableWarningText={tableWarningText}
                                        />
                                    );
                                } else {
                                    const annotation = selectedAnnotations.find(a => getAnnotationKey(a) === id);
                                    if (!annotation || annotation.groupId) return null; // Don't render if annotation is grouped or not found
                                    const isProcessing = annotation.status === 'loading';
                                    return (
                                        <SortableItem key={id} id={id}>
                                            <Box
                                                mt={1}
                                                p={1}
                                                border="1px solid"
                                                borderColor="divider"
                                                borderRadius={1.5}
                                                bgcolor={hoveredKey === id ? 'rgba(59, 188, 167, 0.15)' : 'background.paper'}
                                                data-id={id}
                                                sx={{
                                                    transition: 'transform 0.2s ease, background-color 0.2s ease',
                                                    willChange: 'transform',
                                                    boxShadow: 1,
                                                    '&:hover': {boxShadow: 2, bgcolor: 'action.hover'},
                                                    position: 'relative',
                                                    opacity: isProcessing ? 0.6 : 1
                                                }}
                                                onClick={(e) => {
                                                    if (e.defaultPrevented) return;
                                                    schedulePeek(id);
                                                }}
                                                onDoubleClickCapture={cancelPeek}   // capture so dblclick inside fields cancels jiggle
                                                onMouseEnter={() => onHover?.(id)}
                                                onMouseLeave={() => onHover?.(null)}
                                            >
                                                {isProcessing && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        <CircularProgress size={24} />
                                                    </Box>
                                                )}
                                                <AnnotationItem
                                                    annotation={annotation}
                                                    renderEditableField={(fieldKey, fieldValue, inline = false, bold = false) => {
                                                        let safeValue = fieldValue;
                                                        if (fieldValue && typeof fieldValue === 'object' && 'text' in fieldValue) {
                                                            safeValue = fieldValue.text;
                                                        }
                                                        return renderEditableField(id, null, safeValue, fieldKey, inline, bold);
                                                    }}
                                                    onTablePatch={(patch) => onUpdateAnnotation(id, patch)}   // <-- add this
                                                    onDelete={() => handleDelete(annotation)}
                                                    tableWarningText={tableWarningText}
                                                />
                                            </Box>
                                        </SortableItem>
                                    );
                                }
                            })}
                            {/* END_DROP_ID for dropping at the very end */}
                            <SortableItem key={END_DROP_ID} id={END_DROP_ID}>
                                <Box sx={{height: 30, opacity: 0.1}} data-id={END_DROP_ID}/>
                            </SortableItem>
                        </SortableContext>
                        <DragOverlay>
                            {activeId ? (
                                <Box
                                    p={1}
                                    border="1px solid #ccc"
                                    borderRadius={1}
                                    bgcolor={lighten(theme.palette.primary.contrastText, 0.2)}
                                    sx={{boxShadow: 3, minWidth: '200px'}}
                                >
                                    {(() => {
                                        const annotation = selectedAnnotations.find(a => getAnnotationKey(a) === activeId);
                                        if (annotation) {
                                            return (
                                                <AnnotationItem
                                                    annotation={annotation}
                                                    // Simplified renderEditableField for DragOverlay (not actually editable)
                                                    renderEditableField={(fieldKey, fieldValue) => (
                                                        <Typography variant="body2" sx={{
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'anywhere',
                                                            fontWeight: (fieldKey === 'field' || fieldKey.startsWith('colname')) ? 'bold' : 'normal',
                                                        }}>
                                                            {fieldValue}
                                                        </Typography>
                                                    )}
                                                    tableWarningText={tableWarningText}
                                                />
                                            );
                                        }

                                        const groupIdMatch = activeId.startsWith('group_') ? activeId.slice(6) : null;
                                        if (groupIdMatch) {
                                            const groupName = groupMeta[groupIdMatch]?.name ?? groupIdMatch;
                                            return <Typography>{groupName}</Typography>;
                                        }

                                        return null;
                                    })()}
                                </Box>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    <Typography variant="body2" color={theme.palette.secondary.contrastText}>
                        {t("no_annotation_selected")}
                    </Typography>
                )}
                <GroupContextMenu
                    anchorEl={groupMenuAnchor}
                    open={Boolean(groupMenuAnchor)}
                    onClose={handleGroupMenuClose}
                    onDeleteGroup={(id) => {
                        const children = groupAnnotationOrder[id] || [];

                        // 1. Unselect all annotations belonging to this group
                        selectedAnnotations.forEach(a => {
                            if (a.groupId === id) {
                                onRemoveAnnotation(a);
                            }
                        });

                        // 2. Ungroup children in the master annotation list
                        setAnnotations(prev =>
                            prev.map(a =>
                                children.includes(getAnnotationKey(a)) ? {...a, groupId: null} : a
                            )
                        );

                        // 3. FIX: Update the main order to re-insert the children
                        setAnnotationOrder(prev => {
                            const index = prev.indexOf(`group_${id}`);
                            // Filter out the group ID and any lingering child IDs to prevent duplicates
                            const filtered = prev.filter(k => k !== `group_${id}` && !children.includes(k));

                            if (index === -1) {
                                // Failsafe if group isn't in the main order for some reason
                                return [...filtered, ...children];
                            }

                            // Splice the ungrouped children back into the order array
                            return [
                                ...filtered.slice(0, index),
                                ...children,
                                ...filtered.slice(index)
                            ];
                        });

                        // 4. Clean up group-specific metadata
                        setGroupAnnotationOrder(prev => {
                            const updated = {...prev};
                            delete updated[id];
                            return updated;
                        });
                        setGroupMeta(prev => {
                            const updated = {...prev};
                            delete updated[id];
                            return updated;
                        });

                        handleGroupMenuClose();
                    }}
                    onDissociateGroup={(id) => {
                        const children = groupAnnotationOrder[id] || [];
                        setAnnotationOrder(prev => {
                            const index = prev.indexOf(`group_${id}`);
                            const filtered = prev.filter(k => k !== `group_${id}` && !children.includes(k));
                            return [
                                ...filtered.slice(0, index),
                                ...children,
                                ...filtered.slice(index)
                            ];
                        });
                        setGroupAnnotationOrder(prev => {
                            const updated = {...prev};
                            delete updated[id];
                            return updated;
                        });
                        setGroupMeta(prev => {
                            const updated = {...prev};
                            delete updated[id];
                            return updated;
                        });
                        setAnnotations(prev => prev.map(a => (
                            children.includes(getAnnotationKey(a)) ? {...a, groupId: null} : a
                        )));
                        handleGroupMenuClose();
                    }}
                    groupId={activeGroupId}
                />
            </Box>
        </>
    );
}

export default ResizablePanel;